import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  loadSettings,
  saveSettings,
  selectVoice,
  speak as ariaSpeak,
  replayLast as ariaReplay,
  stopAudio as ariaStopAudio,
  clearReplay as ariaClearReplay,
  primeAudio,
  setTtsUnavailableHandler,
  setReplayAvailableHandler,
  setPlayingHandler,
  cancelSpeech,
  cancelRecognition,
  matchCommand,
  greetingFor,
  buildVisibleGreeting,
  buildSpokenGreeting,
  isSpeechAvailable,
  isRecognitionAvailable,
  TOPOLOGY_PHASE_NARRATION,
  spokenSummary,
  extractRedFlags,
} from "../lib/aria";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { PREVIEW_MODE } from "../components/PreviewNoPHIBanner";
import {
  classifyIntent,
  isUnifiedRouterEnabled,
  ROUTER_CONFIRMATIONS,
  ROUTER_INTENTS,
} from "../lib/ariaRouter";
import { matchVoiceIntent } from "../lib/voiceIntents";
import { getLastCase, clearAllChairsideState } from "../lib/chairsideStore";
import { emit, EVENTS } from "../lib/analytics";
import { correctTranscript } from "../lib/vocab";
import AriaClarifyDialog from "../components/AriaClarifyDialog";

// Spoken warnings for the two Patients intent classes in Preview.
// Kept in this file so the banner copy in PreviewNoPHIBanner stays independent.
const PATIENTS_NAV_PREVIEW_SPEECH =
  "Opening the Patients demonstration. This is the ProsthoAI Preview environment. Do not enter real patient information or PHI. Patient records are disabled during beta testing.";
const PATIENTS_ENTRY_PREVIEW_SPEECH =
  "Patient record entry is disabled in the ProsthoAI Preview environment. Do not enter real patient information or PHI. I can demonstrate the workflow only.";

const AriaCtx = createContext(null);

// ─── Event-accurate microphone constants (2026-08-16, mobile P0) ─────
// The clinician's UI text must reflect actual SR lifecycle events, not
// "we asked for the mic". These constants and the diagnostic-trace
// helper below are the sole plumbing added for that correction.
//
//   AUDIO_READINESS_TIMEOUT_MS
//     After `onstart` fires we start this timer. If `onaudiostart` has
//     not confirmed audio capture within the window, we abort with a
//     synthetic audio-capture error and surface the "no microphone
//     audio detected" copy. Timer is NEVER armed before `onstart` —
//     doing so would race the browser permission prompt. 2500 ms is
//     conservative on iOS Safari (typical audiostart lag ~200-600 ms).
//
//   MAX_KEEP_ALIVE_RESTARTS
//     iOS Safari silently ends `webkitSpeechRecognition` every ~15-20 s.
//     Founder brief: NO silent 8× retry. Cap at ONE keep-alive restart
//     for the benign `no-speech`/`aborted` case; permission-denied and
//     audio-capture never restart. A second unexpected end surfaces
//     the paused-with-Continue UI.
const AUDIO_READINESS_TIMEOUT_MS = 2500;
const MAX_KEEP_ALIVE_RESTARTS = 1;

// ─── In-memory diagnostic ring buffer (admin-only surface) ──────────
// SAFETY CONTRACT — this array is the source of truth for
// AriaDebugPanel's Event Trace section. It is subject to the
// following invariants (all four MUST hold at all times):
//   1. Entries contain ONLY: { event: string, t: number }
//      where t is a relative millisecond timestamp measured from the
//      user's tap. NEVER a transcript, question, answer, patient
//      information, audio sample, or any clinical content.
//   2. Storage is process-memory only. NEVER localStorage,
//      sessionStorage, IndexedDB, cookies, logs, analytics, network
//      requests, or DB writes. On page reload the buffer starts empty.
//   3. Bounded to ARIA_DIAG_MAX. Oldest entries are dropped when the
//      buffer overflows so a long session cannot grow memory.
//   4. Access is gated at the READER layer (AriaDebugPanel) on the
//      ADMIN JWT flag AND the `?ariaDebug=1` query string. Ordinary
//      clinicians never see it.
const ARIA_DIAG_MAX = 64;
function initAriaDiag() {
  if (typeof window === "undefined") return;
  if (!window.__ariaDiag || !Array.isArray(window.__ariaDiag.trace)) {
    window.__ariaDiag = {
      trace: [],
      readiness_timeout_ms: AUDIO_READINESS_TIMEOUT_MS,
      max_keep_alive_restarts: MAX_KEEP_ALIVE_RESTARTS,
    };
  }
}
function ariaDiagPush(event, tapMark) {
  try {
    initAriaDiag();
    const t =
      typeof tapMark === "number"
        ? Math.max(0, Math.round((performance.now ? performance.now() : Date.now()) - tapMark))
        : 0;
    const trace = window.__ariaDiag.trace;
    trace.push({ event: String(event), t });
    if (trace.length > ARIA_DIAG_MAX) trace.splice(0, trace.length - ARIA_DIAG_MAX);
  } catch (_) { /* best-effort */ }
}
function ariaDiagReset() {
  try {
    initAriaDiag();
    window.__ariaDiag.trace.length = 0;
  } catch (_) { /* best-effort */ }
}

export function AriaProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(() => loadSettings());
  const [voice, setVoice] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  // Event-accurate mic surface (2026-08-16, mobile P0).
  //   "idle"      — no session in flight
  //   "starting"  — SR.start() called, onstart fired, waiting for audio
  //   "listening" — onaudiostart confirmed the OS opened the mic
  //   "hearing"   — onspeechstart confirmed the user's voice reached SR
  // The legacy `listening` boolean stays true for BOTH "listening" and
  // "hearing" so callers that only care whether the mic is live keep
  // working unchanged.
  const [micStatus, setMicStatus] = useState("idle");
  const [transcript, setTranscript] = useState("");
  const [originalTranscript, setOriginalTranscript] = useState("");
  const [vocabCorrections, setVocabCorrections] = useState([]);
  const [lastSpoken, setLastSpoken] = useState("");
  const [open, setOpen] = useState(false);
  // Rachel/ElevenLabs TTS availability. When set, the AriaFab card renders
  // "Audio temporarily unavailable" — never a browser-voice substitute.
  //   null   → configured & healthy (default assumption; cleared on success)
  //   { reason } → last known failure from /api/aria/tts
  const [ttsUnavailable, setTtsUnavailable] = useState(null);
  // Whether the last successfully-generated response is still cached and
  // can be replayed without re-hitting the clinical model.
  const [canReplay, setCanReplay] = useState(false);
  // Whether Rachel audio is currently playing (used to show Stop Audio).
  const [isPlaying, setIsPlaying] = useState(false);
  // Module greeting throttle — same slug, same session, only once
  const greetedSlugsRef = useRef(new Set());

  // Register the ARIA TTS "unavailable" bridge exactly once. When the
  // backend proxy returns 503 (missing config, upstream error, quota), the
  // aria.js speak() layer calls this and we surface the banner in the UI.
  useEffect(() => {
    setTtsUnavailableHandler((info) => setTtsUnavailable(info || { reason: "upstream_error" }));
    setReplayAvailableHandler((v) => setCanReplay(!!v));
    setPlayingHandler((v) => setIsPlaying(!!v));
    return () => {
      setTtsUnavailableHandler(null);
      setReplayAvailableHandler(null);
      setPlayingHandler(null);
    };
  }, []);

  // Resolve voice whenever the accent setting changes (or first mount)
  useEffect(() => {
    let alive = true;
    selectVoice(settings.accent).then((v) => { if (alive) setVoice(v); });
    return () => { alive = false; };
  }, [settings.accent]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      saveSettings(next);
      return next;
    });
  }, []);

  const speak = useCallback(async (text) => {
    if (!settings.enabled || !text) return false;
    // Voice-output toggle. When disabled the WRITTEN response is still
    // shown (setLastSpoken below) but Rachel audio is suppressed. Mic
    // listening is unaffected.
    if (settings.spokenResponses === false) {
      setLastSpoken(text);
      return false;
    }
    setLastSpoken(text);
    setSpeaking(true);
    // Latency instrumentation: TTS start / completion (P0, 2026-01)
    try {
      performance.mark("aria:tts_start");
      window.__ariaLatency = window.__ariaLatency || {};
      window.__ariaLatency.tts_start = performance.now();
    } catch (_) { /* perf API best-effort */ }
    let ok = false;
    try {
      ok = await ariaSpeak(text, { voice, rate: settings.rate, pitch: settings.pitch });
      // Only clear the "audio unavailable" banner if playback truly succeeded.
      if (ok) setTtsUnavailable((prev) => (prev ? null : prev));
    } finally {
      setSpeaking(false);
      try {
        performance.mark("aria:tts_end");
        const end = performance.now();
        if (window.__ariaLatency && window.__ariaLatency.tts_start) {
          const dur = Math.round(end - window.__ariaLatency.tts_start);
          window.__ariaLatency.tts_end = end;
          window.__ariaLatency.tts_duration_ms = dur;
          if (typeof console !== "undefined" && console.info) {
            console.info(`[aria-latency] TTS duration: ${dur}ms`);
          }
        }
      } catch (_) { /* perf API best-effort */ }
    }
    return !!ok;
  }, [settings.enabled, settings.spokenResponses, settings.rate, settings.pitch, voice]);

  /** Replay the last full Rachel response from cache — no clinical model call. */
  const replayResponse = useCallback(async () => {
    if (!settings.enabled || settings.spokenResponses === false) return;
    setSpeaking(true);
    try {
      const ok = await ariaReplay();
      if (ok) setTtsUnavailable((prev) => (prev ? null : prev));
    } finally {
      setSpeaking(false);
    }
  }, [settings.enabled, settings.spokenResponses]);

  /** Hard-stop the current audio queue. Keeps Replay available. */
  const stopAudio = useCallback(() => {
    ariaStopAudio();
    setSpeaking(false);
  }, []);

  const stop = useCallback(() => {
    cancelSpeech();
    cancelRecognition();
    // If a recording is in-flight, treat this like Cancel — the mic MUST
    // stop immediately on logout/navigate-away.
    const active = activeRecRef.current;
    if (active) {
      active.aborted = true;
      active.setEndReason("cancel");
      try { active.r.abort(); } catch (_) { /* ignore */ }
      try { active.r.stop(); } catch (_) { /* ignore */ }
    }
    setSpeaking(false);
    setListening(false);
    setMicStatus("idle");
    setRecording(null);
    setReview(null);
  }, []);

  const navigateTo = useCallback((path) => {
    if (path) nav(path);
  }, [nav]);

  // ─── Unified router (Preview pilot, gated by REACT_APP_UNIFIED_ROUTER=1) ───
  // Router-owned state: when the classifier returns intent="clarify" we show
  // the AriaClarifyDialog with three one-tap choices.
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [clarifyTranscript, setClarifyTranscript] = useState("");

  const closeClarify = useCallback(() => {
    setClarifyOpen(false);
    setClarifyTranscript("");
  }, []);

  // ─── Shared clinical conversation state (2026-02 continuity repair) ────
  // Founder requirement: Quick Assist and the floating ARIA overlay share
  //   • one active conversation_id,
  //   • one accumulated slot object (server-side; carried by conversation_id),
  //   • one conversation history [{q,a}],
  //   • one pending-clarification flag.
  // When ARIA has asked a clarifier ("Are you asking about the abutment
  // screw or the prosthetic screw?"), the NEXT microphone utterance must
  // continue that thread — NOT be re-classified as a fresh command.
  // Opening or closing the overlay MUST NOT reset or reroute the thread.
  const newConversationId = () =>
    `qa-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
  const [conversationId, setConversationId] = useState(() => newConversationId());
  const [conversationTurns, setConversationTurns] = useState([]); // [{q,a}]
  const [pendingClarification, setPendingClarification] = useState(false);
  // Refs mirror state so a routing decision made mid-render sees the latest
  // pending-clarification value even before the next paint. Also used inside
  // the SR `beginListen` closure (which is memoised and would otherwise
  // capture a stale snapshot of `conversationTurns`).
  const pendingClarificationRef = useRef(false);
  const conversationIdRef = useRef(conversationId);
  const conversationTurnsRef = useRef([]);
  useEffect(() => { pendingClarificationRef.current = pendingClarification; }, [pendingClarification]);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { conversationTurnsRef.current = conversationTurns; }, [conversationTurns]);

  const appendConversationTurn = useCallback((q, a) => {
    const answer = (a || "").trim();
    setConversationTurns((prev) => [...prev, { q, a: answer }]);
    // A clarifier is any answer that ends with a question mark. When the
    // server asks for the missing slot the answer will read "... prosthetic
    // screw?". The next utterance must route back into THIS thread.
    setPendingClarification(/\?\s*["')\]]?\s*$/.test(answer));
  }, []);

  const resetConversation = useCallback(() => {
    setConversationTurns([]);
    setPendingClarification(false);
    const fresh = newConversationId();
    setConversationId(fresh);
    conversationIdRef.current = fresh;
    pendingClarificationRef.current = false;
  }, []);

  // Route a Quick Assist question — speak a short confirmation, navigate to
  // /quick-assist with the question pre-filled, and auto-submit on arrival.
  // /api/ask remains authoritative for PHI, urgent, and numerical safety
  // checks; the router only decides *where* the utterance goes.
  //
  // `continuation` — when true, we're routing a follow-up answer into an
  // already-open Quick Assist thread. We skip the "Checking Quick Assist"
  // confirmation because a spoken confirmation between the clarifier and
  // the answer feels like ARIA didn't hear the reply.
  const routeToQuickAssist = useCallback((question, { continuation = false } = {}) => {
    const q = (question || "").trim();
    if (!q) return;
    if (!continuation) speak(ROUTER_CONFIRMATIONS.qa);
    navigateTo(`/quick-assist?q=${encodeURIComponent(q)}&auto=1`);
  }, [speak, navigateTo]);

  const routeToCamera = useCallback(() => {
    speak(ROUTER_CONFIRMATIONS.camera);
    navigateTo("/quick-assessment/new");
  }, [speak, navigateTo]);


  const runCommand = useCallback(async (utterance) => {
    if (!utterance) return null;

    // ── Clinical continuity (2026-02 mobile-parity repair) ────────────
    // When ARIA has an unresolved clarifier out, the NEXT utterance is by
    // definition the clinician's answer to that clarifier. Route it back
    // into the same Quick Assist thread WITHOUT re-classifying — a two-
    // word answer like "abutment screw" would otherwise hit CLARIFY and
    // pop the generic "How can I help with that?" overlay.
    if (pendingClarificationRef.current) {
      routeToQuickAssist(utterance, { continuation: true });
      return { path: "/quick-assist", label: "Quick Assist", router: "continuation" };
    }

    // ── Voice-intent verbs (chairside architecture, Feb 2026) ─────────
    // These verbs are ARIA's chairside commands — they don't collide with
    // the classic nav vocabulary because their regex requires an explicit
    // verb ("open", "resume", "take me back", "repeat that", etc.).
    const intent = matchVoiceIntent(utterance);
    if (intent) {
      emit(EVENTS.VOICE_COMMAND, { feature: intent.id, input_mode: "voice" });
      if (intent.kind === "live") {
        if (intent.action === "history_back") {
          try { nav(-1); } catch (_) { /* best-effort */ }
          await speak("Going back.");
          return { path: "back", label: intent.label, router: "voice" };
        }
        if (intent.action === "repeat_last") {
          if (lastSpoken) {
            await speak(lastSpoken);
          } else {
            await speak("I do not have anything to repeat yet.");
          }
          return { path: null, label: intent.label, router: "voice" };
        }
        if (intent.action === "resume_last_case") {
          const last = getLastCase();
          if (last && last.id) {
            await speak("Reopening your last case.");
            navigateTo(`/quick-assessment/${last.id}`);
            return { path: `/quick-assessment/${last.id}`, label: intent.label, router: "voice" };
          }
          await speak("I do not have a recent case to reopen yet.");
          return null;
        }
        if (intent.path) {
          await speak(`Opening ${intent.label}.`);
          navigateTo(intent.path);
          return { path: intent.path, label: intent.label, router: "voice" };
        }
      } else {
        // Stubbed intents — recognised but not yet wired. Route to Ask the
        // Bench so the clinician still gets a useful response.
        await speak(`I heard ${intent.label}. That command is coming soon — routing you to Ask the Bench.`);
        routeToQuickAssist(utterance);
        return { path: "/quick-assist", label: intent.label, router: "voice_stub" };
      }
    }

    // Unified router path (Preview pilot, feature-flagged). When on, ARIA is
    // the single front door: photo/assessment → /quick-assessment,
    // clinical Q&A → /quick-assist (which POSTs /api/ask), navigation →
    // existing runCommand, ambiguous → clarify dialog.
    if (isUnifiedRouterEnabled()) {
      const decision = classifyIntent(utterance, { isAdmin: !!user?.is_admin });
      if (decision.intent === ROUTER_INTENTS.CAMERA) {
        routeToCamera();
        return { path: "/quick-assessment/new", label: "Quick Assessment", router: "camera" };
      }
      if (decision.intent === ROUTER_INTENTS.QA) {
        routeToQuickAssist(utterance);
        return { path: "/quick-assist", label: "Quick Assist", router: "qa" };
      }
      if (decision.intent === ROUTER_INTENTS.NAV && decision.navCommand) {
        const cmd = decision.navCommand;
        if (PREVIEW_MODE && cmd.path === "/patients") {
          const line = cmd.isPatientEntry
            ? PATIENTS_ENTRY_PREVIEW_SPEECH
            : PATIENTS_NAV_PREVIEW_SPEECH;
          await speak(line);
          navigateTo(cmd.path);
          return cmd;
        }
        await speak(`Opening ${cmd.label}.`);
        navigateTo(cmd.path);
        return cmd;
      }
      // Fall-through → clarify UI. No spoken "unknown command."
      setClarifyTranscript(utterance);
      setClarifyOpen(true);
      return null;
    }

    // Legacy nav-only path (router flag OFF).
    const cmd = matchCommand(utterance, { isAdmin: !!user?.is_admin });
    if (!cmd) {
      await speak("I did not catch a known command. You can try saying: new case, occlusion analysis, or show my patients.");
      return null;
    }
    // Preview / no-PHI guardrail: EVERY command that routes to /patients
    // must include a clear spoken warning — navigation and entry alike.
    // Entry-class intents get a stronger message; navigation intents get the
    // "demonstration" framing. Both still route to /patients (never
    // /patients/new). Non-Preview production speech falls through unchanged.
    if (PREVIEW_MODE && cmd.path === "/patients") {
      const line = cmd.isPatientEntry
        ? PATIENTS_ENTRY_PREVIEW_SPEECH
        : PATIENTS_NAV_PREVIEW_SPEECH;
      await speak(line);
      navigateTo(cmd.path);
      return cmd;
    }
    await speak(`Opening ${cmd.label}.`);
    navigateTo(cmd.path);
    return cmd;
  }, [user?.is_admin, speak, navigateTo, routeToCamera, routeToQuickAssist, lastSpoken, nav]);

  // Double-activation guard — ref (not state) so a re-render can't race a
  // second tap within the same frame.
  const listeningRef = useRef(false);
  // Refs owned by the active recording session (so cancel/stop can clear
  // them from any UI surface without racing the internal onend).
  const activeRecRef = useRef(null); // { r, timers: { hardCap, silence, warning, tick }, aborted }
  // Live recording meta for the UI (elapsed timer, warning state).
  const [recording, setRecording] = useState(null); // { elapsed_ms, max_ms, warned }
  /**
   * Review state — non-null after Stop or the 45s hard cap when the caller
   * did NOT pass an onTranscript override. The clinician sees the corrected
   * transcript, vocab corrections, and Cancel / Submit controls. Submit is
   * the only path that dispatches the transcript to /api/ask.
   *   { text, original, corrections, reason: "stop" | "silence" | "max_reached" }
   */
  const [review, setReview] = useState(null);
  // Ref mirror so timers and stopListen callbacks can inspect current
  // recording session without racing state updates.
  const cancelledRef = useRef(false);

  // ARIA voice-recording parameters (Preview, Feb 2026 refinement).
  //   MAX_RECORDING_MS     hard cap; auto-ends and PRESERVES transcript
  //   SILENCE_MS           end-of-turn after N ms of no interim results
  //                        (6s — accommodates clinical thinking pause)
  //   SILENCE_WARNING_MS   after N ms of silence, surface a subtle prompt
  //                        ("Still recording — continue speaking or tap
  //                        Stop") without ending the session. Cleared the
  //                        moment speech resumes.
  //   WARNING_LEAD_MS      fire the 45s-cap warning this long before it
  const MAX_RECORDING_MS = 45_000;
  const SILENCE_MS = 6000;
  const SILENCE_WARNING_MS = 4000;
  const WARNING_LEAD_MS = 5000;

  /**
   * beginListen — P0 chairside-speed change (2026-01):
   *
   * Starts the microphone SYNCHRONOUSLY inside the caller's user-gesture
   * stack (click handler). No awaits, no TTS, no network calls run before
   * `recognition.start()`, which is required by iOS Safari / iPadOS Safari
   * (WebKit) to authorize `webkitSpeechRecognition`.
   *
   * Recording envelope (2026-02 refinement):
   *   • continuous = true so natural pauses do not end the recording
   *   • silence detector — auto-ends after ~4s of no interim results
   *     (accommodates a clinician's thinking pause without terminating)
   *   • hard cap 45s — visible warning fires 5s before the cap; on hard
   *     cap the transcript enters REVIEW MODE, it is NOT auto-submitted
   *   • cancelListen()  aborts and DISCARDS the transcript
   *   • stopListen()    releases the mic, preserves the transcript,
   *                     returns the user to REVIEW MODE — does NOT call
   *                     runCommand / /api/ask
   *   • submitReview()  is the ONLY path that dispatches the transcript
   *                     to runCommand (and therefore /api/ask)
   *   • cancelReview()  clears a held transcript without sending anything
   *
   * @param {object} [opts]
   * @param {(text: string) => void} [opts.onTranscript] - Direct-capture
   *   mode used by code paths that want the raw transcript without going
   *   through review mode (e.g. legacy inline capture). When supplied,
   *   review mode is BYPASSED and the callback receives the corrected
   *   transcript on any organic end. Cancel still discards.
   *
   * Returns true if the mic was started, false otherwise (already listening,
   * settings disabled, recognition unsupported, or start() threw).
   */
  const beginListen = useCallback((opts) => {
    const onTranscript = opts && typeof opts.onTranscript === "function" ? opts.onTranscript : null;
    // ── Quick Assist mic-cutoff correction (2026-08-09) ────────────────
    // Three new opt-in options preserve existing global-FAB behaviour
    // when omitted:
    //   disableSilenceTimeout  when true, the 6s silence timer is NOT
    //                          armed — natural clinical thinking pauses
    //                          never end the session.
    //   onUnexpectedEnd(text, reason, errorCode?)  optional callback
    //                          invoked when the session ends WITHOUT the
    //                          caller explicitly pressing Done — the
    //                          transcript is preserved and delivered to
    //                          the caller so it can offer a Continue-
    //                          listening surface. Reasons:
    //                            "hard_cap_reached"   45s cap
    //                            "sr_ended"           MAX_RESTARTS
    //                                                 exhausted / fresh
    //                                                 SR.start() threw
    //                            "fatal_error"        e.g. not-allowed,
    //                                                 audio-capture,
    //                                                 service-not-allowed,
    //                                                 network. errorCode
    //                                                 is the SR code.
    //                            "no_speech"          stop pressed with
    //                                                 empty transcript.
    //   seedTranscript         string, prepended to the SR final buffer.
    //                          Used when resuming after an unexpected
    //                          end so accumulated speech is preserved.
    // Cancel remains silent — no callback fires.
    // Done (stopListen) with non-empty text → onTranscript(text).
    const disableSilenceTimeout = !!(opts && opts.disableSilenceTimeout);
    const onUnexpectedEnd =
      opts && typeof opts.onUnexpectedEnd === "function" ? opts.onUnexpectedEnd : null;
    const seedTranscript =
      opts && typeof opts.seedTranscript === "string" ? opts.seedTranscript : "";
    if (!settings.enabled) return false;
    if (listeningRef.current) return false; // debounce double-tap
    if (!isRecognitionAvailable()) {
      // No blocking UI. speak() runs async, doesn't gate mic path.
      speak("Voice recognition is not available in this browser. Use Chrome or Edge to speak commands.");
      return false;
    }
    listeningRef.current = true;
    setTranscript("");
    setOriginalTranscript("");
    setVocabCorrections([]);
    setRecording({ elapsed_ms: 0, max_ms: MAX_RECORDING_MS, warned: false, silence_warning: false });
    // A fresh session invalidates any prior review.
    setReview(null);

    // Reset the in-memory diagnostic buffer so each tap produces a clean
    // event trace. tapMark anchors all subsequent event timestamps as
    // RELATIVE milliseconds — never wall-clock, never persisted.
    ariaDiagReset();
    const tapMark = performance.now ? performance.now() : Date.now();
    ariaDiagPush("requested", tapMark);

    // Event-accurate transition: request-issued → "starting". The UI must
    // NOT show "Listening" until onaudiostart fires below.
    setMicStatus("starting");
    setListening(false);

    // Latency instrumentation
    try {
      performance.mark("aria:tap");
      window.__ariaLatency = window.__ariaLatency || {};
      window.__ariaLatency.tap = performance.now();
    } catch (_) { /* perf API best-effort */ }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const lang = settings.accent === "en-GB" ? "en-GB" : "en-AU";
    // Mutable holder — iOS Safari does not reliably accept r.start() on the
    // same SR instance after WebKit has ended it. On keep-alive restart we
    // build a FRESH SR instance and re-point srRef.current at it. Every
    // handler (silence/hard-cap/stopListen/cancelListen) reads srRef.current
    // so a swap is transparent.
    const srRef = { current: null };
    const makeSR = () => {
      const inst = new SR();
      inst.lang = lang;
      inst.interimResults = true;
      inst.maxAlternatives = 1;
      inst.continuous = true;
      return inst;
    };

    let final = seedTranscript ? (seedTranscript.endsWith(" ") ? seedTranscript : seedTranscript + " ") : "";
    let errorCode = null;
    let dispatched = false;
    // Reason the session ended. Defaults to "sr_native_end" — meaning
    // WebKit itself terminated the recognition session (iOS Safari does
    // this every ~15-20s regardless of ongoing speech). Our OWN end paths
    // overwrite this: silence-timeout → "silence", Stop → "stop",
    // Cancel → "cancel", hard-cap → "max_reached". Distinguishing them
    // is what lets us keep-alive restart iOS's silent terminations.
    let endReason = "sr_native_end";
    let restartCount = 0;
    // Founder brief (2026-08-16): NO silent 8× retries. Cap keep-alive
    // restarts at one for benign no-speech / aborted cases; on a second
    // unexpected end the UI surfaces the paused-with-Continue action.
    // not-allowed / service-not-allowed / audio-capture / network are
    // never restart candidates — those exit via _unexpectedEnd immediately.
    const MAX_RESTARTS = MAX_KEEP_ALIVE_RESTARTS;
    const startedAt = Date.now();
    const timers = { hardCap: null, silence: null, silenceWarn: null, warning: null, tick: null, readiness: null };

    const clearAllTimers = () => {
      Object.values(timers).forEach((t) => { if (t) clearTimeout(t); });
      if (timers.tick) { clearInterval(timers.tick); timers.tick = null; }
    };

    const resetSilence = () => {
      // Quick Assist opt-in: skip the silence timer entirely so natural
      // clinical thinking pauses never terminate the session. The hard
      // 45s cap still applies as a safety upper bound.
      if (disableSilenceTimeout) return;
      if (timers.silence) clearTimeout(timers.silence);
      if (timers.silenceWarn) clearTimeout(timers.silenceWarn);
      // Speech just resumed — clear any lingering "still recording" warning.
      setRecording((prev) => (prev && prev.silence_warning ? { ...prev, silence_warning: false } : prev));
      // Show the subtle warning after SILENCE_WARNING_MS of silence, without
      // ending the session. The clinician keeps the mic; they choose Stop.
      timers.silenceWarn = setTimeout(() => {
        setRecording((prev) => (prev ? { ...prev, silence_warning: true } : prev));
      }, SILENCE_WARNING_MS);
      timers.silence = setTimeout(() => {
        endReason = "silence";
        try { srRef.current && srRef.current.stop(); } catch (_) { /* already stopped */ }
      }, SILENCE_MS);
    };

    timers.hardCap = setTimeout(() => {
      // Hard cap — mark reason so onend enters review mode with the
      // "Maximum recording time reached" message. Transcript is preserved.
      endReason = "max_reached";
      try { srRef.current && srRef.current.stop(); } catch (_) { /* already stopped */ }
    }, MAX_RECORDING_MS);
    timers.warning = setTimeout(() => {
      setRecording((prev) => (prev ? { ...prev, warned: true } : prev));
    }, MAX_RECORDING_MS - WARNING_LEAD_MS);
    // Live timer tick for the UI (every 250ms is plenty for a chairside chip)
    timers.tick = setInterval(() => {
      setRecording((prev) => {
        if (!prev) return prev;
        const elapsed = Date.now() - startedAt;
        return { ...prev, elapsed_ms: elapsed };
      });
    }, 250);

    // Bind the same handler set to whichever SR instance is current. The
    // handlers close over `final`, `errorCode`, `endReason`, `restartCount`,
    // etc. so accumulated state survives an instance swap.
    const wireHandlers = (inst) => {
      inst.onstart = () => {
        try {
          performance.mark("aria:listening");
          const now = performance.now();
          if (window.__ariaLatency && window.__ariaLatency.tap && restartCount === 0) {
            const dur = Math.round(now - window.__ariaLatency.tap);
            window.__ariaLatency.listening = now;
            window.__ariaLatency.tap_to_listening_ms = dur;
            if (typeof console !== "undefined" && console.info) {
              console.info(`[aria-latency] tap→listening: ${dur}ms`);
            }
          }
        } catch (_) { /* perf API best-effort */ }
        ariaDiagPush("start", tapMark);
        // Event-accurate: SR started, but the OS microphone has NOT yet
        // been confirmed to be capturing audio. Stay in "starting" until
        // onaudiostart fires. This is when the readiness timeout arms —
        // never before, so a slow browser permission prompt cannot trip
        // a spurious "no audio detected" error.
        if (timers.readiness) clearTimeout(timers.readiness);
        timers.readiness = setTimeout(() => {
          // audiostart never arrived within the readiness window.
          // Surface a synthetic audio-capture error so the caller can
          // present "No microphone audio detected. Tap the microphone
          // to try again." No auto-retry — the clinician decides.
          ariaDiagPush("timeout", tapMark);
          errorCode = "no-audio";
          endReason = "no_audio";
          if (activeRecRef.current) activeRecRef.current.aborted = false;
          try { srRef.current && srRef.current.abort(); } catch (_) { /* already stopped */ }
          try { srRef.current && srRef.current.stop(); } catch (_) { /* already stopped */ }
        }, AUDIO_READINESS_TIMEOUT_MS);
        // Do NOT flip listening=true here. That is now done by onaudiostart.
        resetSilence();
      };
      inst.onaudiostart = () => {
        ariaDiagPush("audiostart", tapMark);
        if (timers.readiness) { clearTimeout(timers.readiness); timers.readiness = null; }
        setListening(true);
        setMicStatus("listening");
      };
      inst.onspeechstart = () => {
        ariaDiagPush("speechstart", tapMark);
        setMicStatus("hearing");
      };
      inst.onresult = (e) => {
        ariaDiagPush("result", tapMark);
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const t = e.results[i][0].transcript;
          if (e.results[i].isFinal) final += t;
          else setTranscript(t);
        }
        // Any interim/final result restarts the silence countdown.
        resetSilence();
      };
      inst.onerror = (e) => {
        // Capture the SR error code for the ?ariaDebug=1 diagnostic overlay
        // AND surface it to the user (via onend → /quick-assist?err=<code>) so
        // mic attempts never fail silently in Preview.
        errorCode = (e && e.error) || "unknown";
        ariaDiagPush(`error:${errorCode}`, tapMark);
        if (timers.readiness) { clearTimeout(timers.readiness); timers.readiness = null; }
        try {
          window.__ariaLatency = window.__ariaLatency || {};
          window.__ariaLatency.recognition_error_code = errorCode;
          window.__ariaLatency.recognition_error_at = performance.now();
          if (typeof console !== "undefined" && console.info) {
            console.info(`[aria-latency] recognition error: ${errorCode}`);
          }
        } catch (_) { /* perf API best-effort */ }
        /* onend still fires; dispatch happens there */
      };
      inst.onend = () => {
        ariaDiagPush("end", tapMark);
        if (timers.readiness) { clearTimeout(timers.readiness); timers.readiness = null; }
        if (dispatched) return;
        const elapsed = Date.now() - startedAt;
        const explicitEnd =
          endReason === "stop" ||
          endReason === "cancel" ||
          endReason === "max_reached" ||
          endReason === "silence" ||
          endReason === "no_audio";
        // iOS Safari fires `onerror` with `no-speech` when its internal ~15-20s
        // window sees no speech energy at the end of the buffer, and `aborted`
        // when we call abort/stop. Both are benign restart candidates.
        // `not-allowed`, `service-not-allowed`, `audio-capture` and `network`
        // are fatal — never restart on those.
        const benignError =
          !errorCode || errorCode === "no-speech" || errorCode === "aborted";

        // ── iOS SAFARI KEEP-ALIVE ─────────────────────────────────────
        // WebKit silently terminates the SR session every ~15-20s. If we
        // detect the "SR ended on its own" case (our own timers / actions
        // never fired) and we're still under the 45s hard cap, spin up a
        // FRESH SR instance and start it. `final` accumulates across
        // restarts via closure so the transcript stays continuous.
        if (
          !explicitEnd &&
          benignError &&
          elapsed < MAX_RECORDING_MS &&
          restartCount < MAX_RESTARTS &&
          activeRecRef.current &&
          activeRecRef.current.aborted !== true
        ) {
          restartCount += 1;
          errorCode = null;
          ariaDiagPush(`restart:${restartCount}`, tapMark);
          try {
            window.__ariaLatency = window.__ariaLatency || {};
            window.__ariaLatency.recognition_restarts = restartCount;
            if (typeof console !== "undefined" && console.info) {
              console.info(
                `[aria-mic] iOS keep-alive restart #${restartCount} at ${elapsed}ms`,
              );
            }
          } catch (_) { /* logging best-effort */ }
          // Small delay lets WebKit's audio subsystem release before we
          // reopen it — without this iOS often throws InvalidStateError.
          setTimeout(() => {
            if (dispatched || (activeRecRef.current && activeRecRef.current.aborted)) return;
            try {
              const fresh = makeSR();
              wireHandlers(fresh);
              srRef.current = fresh;
              if (activeRecRef.current) activeRecRef.current.r = fresh;
              fresh.start();
            } catch (err) {
              // Restart truly failed — dispatch whatever we have so the
              // clinician isn't left with a spinning mic and no output.
              try {
                if (typeof console !== "undefined" && console.warn) {
                  console.warn("[aria-mic] keep-alive restart failed:", err);
                }
              } catch (_) { /* console best-effort */ }
              // Quick Assist opt-in: preserve the transcript & hand the
              // recovery decision to the caller. NEVER auto-submit.
              if (onUnexpectedEnd) {
                _unexpectedEnd("sr_ended");
                return;
              }
              // Trigger onend one more time on a no-op instance so the
              // regular dispatch path runs. Simpler: dispatch inline.
              // The inline dispatch mirrors the block below.
              _dispatch();
            }
          }, 120);
          return;
        }

        // Quick Assist opt-in: onend when the caller wants to be
        // notified of a session ending WITHOUT its own Done tap.
        // Applies when: MAX_RESTARTS exhausted (still under cap), hard
        // cap reached, or a fatal SR error surfaced. Cancel remains
        // silent; explicit Done routes through onTranscript.
        if (onUnexpectedEnd) {
          if (endReason === "no_audio") {
            // Readiness timeout expired without onaudiostart. Surface
            // as a fatal audio-capture error so QuickAssist shows the
            // "No microphone audio detected" copy and the retry
            // control. Never auto-restart.
            _unexpectedEnd("fatal_error", "no-audio");
            return;
          }
          if (errorCode && !benignError) {
            _unexpectedEnd("fatal_error", errorCode);
            return;
          }
          if (endReason === "max_reached") {
            _unexpectedEnd("hard_cap_reached");
            return;
          }
          if (endReason === "silence") {
            // disableSilenceTimeout guarantees this cannot happen for
            // Quick Assist, but keep the branch defensive.
            _unexpectedEnd("silence");
            return;
          }
          if (
            !explicitEnd &&
            benignError &&
            restartCount >= MAX_RESTARTS
          ) {
            _unexpectedEnd("sr_ended");
            return;
          }
        }

        _dispatch();
      };
    };

    // Inline dispatcher used by both natural onend and failed-restart paths.
    // Everything below the keep-alive branch lives here so both call sites
    // stay in lockstep.
    const _dispatch = () => {
      if (dispatched) return;
      dispatched = true;
      clearAllTimers();
      const wasAborted = activeRecRef.current?.aborted === true;
      activeRecRef.current = null;
      try {
        performance.mark("aria:speech_end");
        const now = performance.now();
        if (window.__ariaLatency && window.__ariaLatency.listening) {
          const dur = Math.round(now - window.__ariaLatency.listening);
          window.__ariaLatency.speech_end = now;
          window.__ariaLatency.listening_to_speech_end_ms = dur;
          window.__ariaLatency.recognition_restarts = restartCount;
          if (typeof console !== "undefined" && console.info) {
            console.info(`[aria-latency] listening→speech_end: ${dur}ms (restarts=${restartCount})`);
          }
        }
      } catch (_) { /* perf API best-effort */ }
      listeningRef.current = false;
      setListening(false);
      setMicStatus("idle");
      setRecording(null);
      // Cancel → discard everything, no dispatch, no review, silence.
      if (wasAborted || endReason === "cancel") {
        setTranscript("");
        setOriginalTranscript("");
        setVocabCorrections([]);
        setReview(null);
        return;
      }
      const rawText = final.trim();
      // ── ARIA Vocabulary Engine (Feb 2026) ──
      const priorText = (conversationTurnsRef.current || [])
        .flatMap((t) => [t.q || "", t.a || ""])
        .join(" ")
        .slice(-500);
      const vocab = correctTranscript(rawText, {
        minConfidence: "medium",
        extraContext: priorText,
      });
      const text = vocab.corrected || rawText;
      setOriginalTranscript(rawText);
      setVocabCorrections(vocab.corrections || []);
      setTranscript(text);
      if (onTranscript) {
        // Quick Assist opt-in: never invoke onTranscript with an empty
        // string — that would auto-submit an empty question. Route the
        // no-speech case through onUnexpectedEnd instead.
        if (!text && onUnexpectedEnd) {
          try { onUnexpectedEnd("", "no_speech", errorCode || null); } catch (_) { /* caller */ }
          return;
        }
        try { onTranscript(text); } catch (_) { /* caller error is not ours */ }
        return;
      }
      if (!text) {
        if (isUnifiedRouterEnabled()) {
          const code = errorCode || "no-speech";
          try { nav(`/quick-assist?err=${encodeURIComponent(code)}`); } catch (_) { /* nav */ }
        }
        return;
      }
      // ── REVIEW MODE ─────────────────────────────────────────────
      // Stop / silence / max_reached / sr_native_end (after MAX_RESTARTS)
      // → hold the transcript for review. Only submitReview() will
      // dispatch it to runCommand / /api/ask.
      setReview({
        text,
        original: rawText,
        corrections: vocab.corrections || [],
        reason: endReason,
        at: Date.now(),
      });
    };

    // Quick-Assist opt-in helper. Delivers the preserved accumulated
    // transcript to the caller WITHOUT running dispatch (no review, no
    // onTranscript). Cancel remains silent — this helper is never
    // invoked when the caller has aborted.
    const _unexpectedEnd = (reason, code) => {
      if (dispatched) return;
      dispatched = true;
      clearAllTimers();
      const wasAborted = activeRecRef.current?.aborted === true;
      activeRecRef.current = null;
      listeningRef.current = false;
      setListening(false);
      setMicStatus("idle");
      setRecording(null);
      if (wasAborted || endReason === "cancel") {
        setTranscript("");
        setOriginalTranscript("");
        setVocabCorrections([]);
        setReview(null);
        return;
      }
      const rawText = final.trim();
      const priorText = (conversationTurnsRef.current || [])
        .flatMap((t) => [t.q || "", t.a || ""])
        .join(" ")
        .slice(-500);
      const vocab = correctTranscript(rawText, {
        minConfidence: "medium",
        extraContext: priorText,
      });
      const text = vocab.corrected || rawText;
      setOriginalTranscript(rawText);
      setVocabCorrections(vocab.corrections || []);
      setTranscript(text);
      try { onUnexpectedEnd(text, reason, code || errorCode || null); } catch (_) { /* caller */ }
    };

    // Boot the first SR instance and register it on the active-recording ref
    // so stopListen / cancelListen can talk to the current instance.
    srRef.current = makeSR();
    wireHandlers(srRef.current);

    activeRecRef.current = {
      r: srRef.current,
      timers,
      aborted: false,
      clearAllTimers,
      setEndReason: (reason) => { endReason = reason; },
    };

    // Synchronous start — MUST be the last statement before returning so
    // WebKit still recognises the caller's gesture as authorising the mic.
    // Use srRef.current so the same start path works for the initial launch
    // AND for the keep-alive-swapped instance semantics.
    try {
      srRef.current.start();
    } catch (_) {
      ariaDiagPush("error:start-failed", tapMark);
      clearAllTimers();
      activeRecRef.current = null;
      srRef.current = null;
      listeningRef.current = false;
      setMicStatus("idle");
      setRecording(null);
      if (isUnifiedRouterEnabled()) {
        try { nav(`/quick-assist?err=start-failed`); } catch (_) { /* nav best-effort */ }
      }
      return false;
    }
    return true;
  }, [settings.enabled, settings.accent, speak, nav]);

  /** Stop the mic and hold the transcript in review mode (NO dispatch). */
  const stopListen = useCallback(() => {
    const active = activeRecRef.current;
    if (!active) return;
    active.setEndReason("stop");
    try { active.r.stop(); } catch (_) { /* already stopped */ }
  }, []);

  /** Stop the mic and DISCARD the transcript (Cancel). */
  const cancelListen = useCallback(() => {
    const active = activeRecRef.current;
    if (!active) return;
    active.aborted = true;
    active.setEndReason("cancel");
    try { active.r.abort(); } catch (_) { /* fall back to stop */ }
    try { active.r.stop(); } catch (_) { /* already stopped */ }
  }, []);

  /**
   * Submit the held review transcript to the ARIA dispatcher.
   * This is the ONLY path that sends the transcript to runCommand /
   * /api/ask. If no review is in flight, no-op.
   */
  const submitReview = useCallback(() => {
    const r = review;
    if (!r || !r.text) return;
    setReview(null);
    runCommand(r.text).catch(() => { /* runCommand handles its own errors */ });
  }, [review, runCommand]);

  /** Discard the held review transcript without sending anything. */
  const cancelReview = useCallback(() => {
    setReview(null);
    setTranscript("");
    setOriginalTranscript("");
    setVocabCorrections([]);
  }, []);

  // Backward-compat: async wrapper that resolves when SR ends.
  const listenOnce = useCallback(() => {
    return new Promise((resolve) => {
      if (!beginListen()) return resolve(null);
      // Poll listeningRef via a lightweight interval; SR.onend flips it back.
      const iv = setInterval(() => {
        if (!listeningRef.current) { clearInterval(iv); resolve(null); }
      }, 60);
    });
  }, [beginListen]);

  const greet = useCallback(async () => {
    if (!settings.enabled) return false;
    const body = isRecognitionAvailable()
      ? "Ready to support your case analysis. What would you like to work on today?"
      : "Ready to support your case analysis. Voice recognition is not available in this browser — you can still ask me to navigate from the dashboard.";
    const line = buildSpokenGreeting(user?.full_name, { body });
    const ok = await speak(line);
    return !!ok;
  }, [settings.enabled, user?.full_name, speak]);

  // ────────────────────────────────────────────────────────────
  // One-shot login greeting (KISS revision, 2026-08-09).
  //
  // AuthContext sets sessionStorage["aria_greet_pending"] = "pending" the
  // moment a fresh password or 2FA-completed login succeeds — BEFORE
  // setUser, so this effect sees the flag on the same render it first
  // sees the authenticated user.
  //
  // Atomic lifecycle on that render:
  //   1. Read the flag. If it is not exactly "pending", exit.
  //   2. Flip the flag to "done" SYNCHRONOUSLY — before any toast, await,
  //      or speech call. This is the sole idempotency guard: a React
  //      StrictMode double invocation or a provider remount finds "done"
  //      and returns.
  //   3. Show the visible greeting toast exactly once.
  //   4. If ARIA is disabled or spoken responses are off — STOP. No
  //      speech, no tap-to-hear action.
  //   5. Otherwise await greet(). If it succeeds, we're done. If it
  //      returns false (audio still locked on iOS), surface a one-shot
  //      tap-to-hear action toast. The flag is NOT reverted, so a
  //      refresh or navigation never re-fires a failed greeting.
  //
  // Logout removes the flag entirely (AuthContext), so a subsequent
  // successful login creates a new "pending" and greets again.
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    let pending = null;
    try { pending = sessionStorage.getItem("aria_greet_pending"); } catch { /* ignore */ }
    if (pending !== "pending") return;
    // Atomic transition — MUST run before any toast/await/speech call.
    try {
      sessionStorage.setItem("aria_greet_pending", "done");
      // Also record when we transitioned so MobileRouteGuard can hold
      // its "workflow available on desktop" toast for a short window
      // and let the login greeting land without visual overlap.
      sessionStorage.setItem("aria_greet_done_at", String(Date.now()));
    } catch { /* ignore */ }

    const visible = buildVisibleGreeting(user?.full_name);
    try { toast.success(visible, { id: "aria-login-greeting" }); } catch { /* toast best-effort */ }
    setLastSpoken(visible);

    const canSpeak = !!settings.enabled && settings.spokenResponses !== false;
    if (!canSpeak) return;

    (async () => {
      const spoke = await greet();
      if (spoke) return;
      // Audio was not unlocked (iOS Safari) — surface a one-shot tap-to-
      // hear action toast. When the clinician taps, primeAudio runs
      // inside their gesture and greet() is retried. The flag is not
      // reverted, so a refresh or navigation NEVER re-fires this action.
      try {
        toast("Tap to hear ARIA welcome you", {
          id: "aria-login-greeting-tap",
          duration: 12000,
          action: {
            label: "Tap to hear",
            onClick: async () => {
              try { primeAudio(); } catch { /* best-effort */ }
              await greet();
            },
          },
        });
      } catch { /* toast best-effort */ }
    })();
  }, [user, settings.enabled, settings.spokenResponses, greet]);

  // ────────────────────────────────────────────────────────────
  // Topology-specific helpers (used by TopologyAnalysisDetail.jsx)
  // ────────────────────────────────────────────────────────────
  const narrateTopologyPhase = useCallback(async (phase) => {
    if (!settings.enabled || !settings.topologyNarration) return;
    const msg = TOPOLOGY_PHASE_NARRATION[phase];
    if (msg) await speak(msg);
  }, [settings.enabled, settings.topologyNarration, speak]);

  const announceTopologyComplete = useCallback(async (reportText) => {
    if (!settings.enabled || !settings.topologyNarration) return;
    await speak("Analysis complete. Reading the findings now.");
    const flags = extractRedFlags(reportText);
    if (flags.length && settings.redFlagAlerts) {
      await speak(`Attention. I have identified ${flags.length} potential clinical concern${flags.length === 1 ? "" : "s"}.`);
      for (const f of flags) {
        await speak(f);
      }
    }
    await speak(spokenSummary(reportText));
  }, [settings.enabled, settings.topologyNarration, settings.redFlagAlerts, speak]);

  const summarizeOnDemand = useCallback(async (reportText) => {
    if (!settings.enabled) return;
    await speak(spokenSummary(reportText));
  }, [settings.enabled, speak]);

  // Module greeting (debounced per-slug per-session)
  const greetModule = useCallback(async (slug) => {
    if (!settings.enabled || !settings.moduleGreetings || !slug) return;
    if (greetedSlugsRef.current.has(slug)) return;
    greetedSlugsRef.current.add(slug);
    const msg = greetingFor(slug);
    if (msg) {
      // small delay so page paints first
      await new Promise((r) => setTimeout(r, 400));
      await speak(msg);
    }
  }, [settings.enabled, settings.moduleGreetings, speak]);

  // ────────────────────────────────────────────────────────────
  // Daily huddle briefing — fetched from backend, spoken on demand
  // ────────────────────────────────────────────────────────────
  const [huddle, setHuddle] = useState(null);
  const [huddleLoading, setHuddleLoading] = useState(false);
  const huddleSpokenRef = useRef(false);

  const fetchHuddle = useCallback(async () => {
    if (!user) return null;
    setHuddleLoading(true);
    try {
      const r = await api.get("/aria/huddle");
      setHuddle(r.data);
      return r.data;
    } catch (_) {
      return null;
    } finally {
      setHuddleLoading(false);
    }
  }, [user]);

  const playHuddle = useCallback(async ({ force = false } = {}) => {
    if (!settings.enabled) return;
    let data = huddle;
    if (!data || force) {
      data = await fetchHuddle();
    }
    if (data?.spoken) {
      await speak(data.spoken);
    }
  }, [settings.enabled, huddle, fetchHuddle, speak]);

  // Auto-play huddle once per session on first authenticated render, if enabled.
  useEffect(() => {
    if (!user || !settings.enabled || !settings.huddleAtLogin) return;
    if (huddleSpokenRef.current) return;
    huddleSpokenRef.current = true;
    // Slight delay so the dashboard mounts first
    const t = setTimeout(() => { playHuddle(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, settings.enabled, settings.huddleAtLogin]);

  // If the founder toggles "ARIA spoken responses" OFF while Rachel is
  // speaking, cut the audio and clear the replay cache immediately. Text
  // continues to render — only the audio side is silenced.
  useEffect(() => {
    if (settings.spokenResponses === false) {
      try { ariaStopAudio(); } catch (_) { /* best-effort */ }
      try { ariaClearReplay(); } catch (_) { /* best-effort */ }
      setSpeaking(false);
    }
  }, [settings.spokenResponses]);

  // Cancel everything on logout
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const prev = prevUserIdRef.current;
    const cur = user?.id || null;
    prevUserIdRef.current = cur;

    if (!user) {
      stop();
      greetedSlugsRef.current.clear();
      huddleSpokenRef.current = false;
      setHuddle(null);
      // Reset shared clinical conversation on logout — the next login is a
      // new user; a stale pending-clarification would misroute their first
      // utterance into the previous session's thread.
      setConversationTurns([]);
      setPendingClarification(false);
      const fresh = newConversationId();
      setConversationId(fresh);
      conversationIdRef.current = fresh;
      pendingClarificationRef.current = false;
      // Only wipe chairside state on an ACTUAL logout (prev user → null).
      // First-mount-without-auth is the boot path; don't destroy state that
      // was just seeded (favorites, last case, timer) or the resume card
      // will race the initial auth resolution.
      if (prev) {
        try { clearAllChairsideState(); } catch (_) { /* ignore */ }
      }
      return;
    }
    // Fire session.start once per user boot / user change. Metadata-only.
    if (prev !== cur) {
      try { emit(EVENTS.SESSION_START, { feature: "session" }); } catch (_) { /* ignore */ }
    }
  }, [user, stop]);

  const value = useMemo(() => ({
    settings,
    updateSettings,
    voice,
    speaking,
    listening,
    micStatus,
    recording,
    review,
    transcript,
    originalTranscript,
    vocabCorrections,
    lastSpoken,
    open,
    setOpen,
    speak,
    stop,
    listenOnce,
    beginListen,
    stopListen,
    cancelListen,
    submitReview,
    cancelReview,
    greet,
    runCommand,
    narrateTopologyPhase,
    announceTopologyComplete,
    summarizeOnDemand,
    greetModule,
    huddle,
    huddleLoading,
    fetchHuddle,
    playHuddle,
    ttsUnavailable,
    canReplay,
    isPlaying,
    replayResponse,
    stopAudio,
    primeAudio,
    speechAvailable: isSpeechAvailable(),
    recognitionAvailable: isRecognitionAvailable(),
    // Shared clinical conversation state (Quick Assist + FAB share the same thread).
    conversationId,
    conversationTurns,
    pendingClarification,
    appendConversationTurn,
    resetConversation,
  }), [
    settings, updateSettings, voice, speaking, listening, micStatus, recording, review, transcript,
    originalTranscript, vocabCorrections, lastSpoken, open,
    speak, stop, listenOnce, beginListen, stopListen, cancelListen, submitReview, cancelReview, greet, runCommand,
    narrateTopologyPhase, announceTopologyComplete, summarizeOnDemand, greetModule,
    huddle, huddleLoading, fetchHuddle, playHuddle, ttsUnavailable,
    canReplay, isPlaying, replayResponse, stopAudio,
    conversationId, conversationTurns, pendingClarification, appendConversationTurn, resetConversation,
  ]);

  return (
    <AriaCtx.Provider value={value}>
      {children}
      <AriaClarifyDialog
        open={clarifyOpen}
        transcript={clarifyTranscript}
        onAsk={() => {
          const t = clarifyTranscript;
          closeClarify();
          routeToQuickAssist(t);
        }}
        onCamera={() => {
          closeClarify();
          routeToCamera();
        }}
        onCancel={closeClarify}
      />
    </AriaCtx.Provider>
  );
}

export const useAria = () => {
  const ctx = useContext(AriaCtx);
  if (!ctx) {
    // Fallback no-op context so non-wrapped pages don't crash in tests
    return {
      settings: loadSettings(),
      updateSettings: () => {},
      voice: null,
      speaking: false,
      listening: false,
      micStatus: "idle",
      transcript: "",
      originalTranscript: "",
      vocabCorrections: [],
      lastSpoken: "",
      recording: null,
      review: null,
      open: false,
      setOpen: () => {},
      speak: async () => {},
      stop: () => {},
      listenOnce: async () => null,
      beginListen: () => false,
      stopListen: () => {},
      cancelListen: () => {},
      submitReview: () => {},
      cancelReview: () => {},
      greet: async () => {},
      runCommand: async () => null,
      narrateTopologyPhase: async () => {},
      announceTopologyComplete: async () => {},
      summarizeOnDemand: async () => {},
      greetModule: async () => {},
      huddle: null,
      huddleLoading: false,
      fetchHuddle: async () => null,
      playHuddle: async () => {},
      ttsUnavailable: null,
      canReplay: false,
      isPlaying: false,
      replayResponse: async () => {},
      stopAudio: () => {},
      primeAudio: () => {},
      speechAvailable: false,
      recognitionAvailable: false,
      conversationId: "",
      conversationTurns: [],
      pendingClarification: false,
      appendConversationTurn: () => {},
      resetConversation: () => {},
    };
  }
  return ctx;
};
